import { LoadingData } from "@senciamatch/frontend/components/LoadingData";
import { trpc } from "@senciamatch/frontend/libs/trpc";
import { formatBuildingShift } from "@senciamatch/frontend/utils/shiftFormat";
import type { WorkShiftGroup } from "@senciamatch/shared/models/workShiftGroup";
import { useState } from "react";

interface Props {
  workShiftGroup: WorkShiftGroup;
  onClose: () => void;
}

export const HistoryModal = ({ workShiftGroup, onClose }: Props) => {
  const [page, setPage] = useState(0);
  const limit = 20;

  const getEditHistory = trpc.workShiftEdit.getEditHistory.useQuery({
    workShiftGroupId: workShiftGroup.id,
    limit,
    offset: page * limit,
  });

  const handleClose = () => {
    onClose();
  };

  if (getEditHistory.isLoading) return <LoadingData />;
  if (!getEditHistory.data) return null;

  const { histories, total, hasMore } = getEditHistory.data;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[100]"
      onClick={handleClose}
      onKeyDown={handleClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-[800px] max-w-[90%] max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="bg-[#EBEBEB] rounded-t-lg flex justify-between items-center p-4 relative flex-shrink-0">
          <h2 className="text-xl font-bold">変更履歴</h2>
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <title>閉じる</title>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {histories.length === 0 ? (
            <div className="text-center text-gray-500 py-8">変更履歴がありません</div>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 mb-4">全{total}件の変更履歴</div>
              <div className="space-y-3">
                {histories.map((history) => (
                  <div key={history.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="text-sm text-gray-600 mb-2">
                      {new Date(history.createdAt).toLocaleString("ja-JP", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                    <div className="font-medium mb-2">{formatBuildingShift(history.workShift)}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-red-600">{history.workShift.previousStaffName || "未割当"}</span>
                      <span>→</span>
                      <span className="text-green-600">{history.workShift.newStaffName || "未割当"}</span>
                    </div>
                  </div>
                ))}
              </div>

              {(page > 0 || hasMore) && (
                <div className="flex justify-center gap-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300"
                  >
                    前のページ
                  </button>
                  <span className="py-2">ページ {page + 1}</span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!hasMore}
                    className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300"
                  >
                    次のページ
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
